import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, StatusPill, formatCurrency } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { Layout, Topbar } from '../components/Layout';
import { ReferrerForm, type ReferrerRow } from '../components/CrmForms';

export function Referrers() {
  const { t, i18n } = useTranslation();
  const { currency } = useOrg();
  const [rows, setRows] = useState<ReferrerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ReferrerRow | null>(null);

  const load = useCallback(async () => {
    const r = await supabase.from('referrers').select('id,name,email,phone,commission_type,commission_value,commission_basis,currency,active,notes').is('deleted_at', null).order('name');
    setRows((r.data as ReferrerRow[]) ?? []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const rule = (r: ReferrerRow) =>
    (r.commission_type === 'percentage' ? `${r.commission_value}%` : formatCurrency(r.commission_value, r.currency ?? currency, i18n.language)) +
    ` · ${t('crm.commBasis.' + r.commission_basis)}`;

  return (
    <Layout header={<Topbar actions={<Button onClick={() => { setEditing(null); setFormOpen(true); }}>+ {t('crm.newReferrer')}</Button>} />}>
      <div className="px-7 pt-6">
        <h1 className="text-[30px] font-semibold tracking-[-0.02em]">{t('nav.referrers')}</h1>
        <p className="mt-1 text-sm text-muted">{t('crm.commissionHint')}</p>
      </div>
      <div className="px-7 pb-7 pt-[18px]">
        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead><tr className="text-left text-xs text-muted">
                <th className="px-5 py-3 font-medium">{t('form.name')}</th>
                <th className="px-2 py-3 font-medium">{t('col.email')}</th>
                <th className="px-2 py-3 font-medium">{t('crm.commissionRule')}</th>
                <th className="px-2 py-3 font-medium">{t('crm.active')}</th>
                <th className="px-5 py-3" />
              </tr></thead>
              <tbody>
                {loading ? <tr><td colSpan={5} className="px-5 py-6 text-muted">{t('common.loading')}</td></tr>
                  : rows.length === 0 ? <tr><td colSpan={5} className="px-5 py-6 text-muted">{t('common.noData')}</td></tr>
                  : rows.map((r) => (
                    <tr key={r.id} className="border-t border-divider hover:bg-bg">
                      <td className="px-5 py-3.5 font-medium">{r.name}</td>
                      <td className="px-2 py-3.5 font-mono text-xs text-muted">{r.email ?? '—'}</td>
                      <td className="px-2 py-3.5">{rule(r)}</td>
                      <td className="px-2 py-3.5">{r.active ? <StatusPill tone="done">{t('common.yes')}</StatusPill> : <StatusPill tone="open">{t('common.no')}</StatusPill>}</td>
                      <td className="px-5 py-3.5 text-right"><Button variant="ghost" size="sm" onClick={() => { setEditing(r); setFormOpen(true); }}>{t('form.edit')}</Button></td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
      <ReferrerForm open={formOpen} onClose={() => setFormOpen(false)} onSaved={load} initial={editing} />
    </Layout>
  );
}
